import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from './../axiosInstance.js';
import moment from 'moment';
import "moment/locale/ru"; // Import the Russian locale
import "./Logbook.css"; // Import custom CSS for styling
import { Button, Paper, TextInput } from "@mantine/core";

moment.locale("ru");

// Convert between the stored "DD.MM.YYYY HH:mm" format and the native
// datetime-local input's "YYYY-MM-DDTHH:mm". The native input handles
// all keyboard quirks, mobile pickers, and validation itself — we only
// bridge the format at the component edge.
const toDatetimeLocal = (value) => {
  if (!value) return "";
  const m = moment(value, ["DD.MM.YYYY HH:mm", "D MMM YYYY HH:mm"], true);
  return m.isValid() ? m.format("YYYY-MM-DDTHH:mm") : "";
};
const fromDatetimeLocal = (value) => {
  if (!value) return "";
  const m = moment(value, "YYYY-MM-DDTHH:mm", true);
  return m.isValid() ? m.format("DD.MM.YYYY HH:mm") : "";
};

const Logbook = ({ art, initialDateTimes, language, releaseDate: releaseDateProp }) => {
  const [dates, setDates] = useState(initialDateTimes || []);
  // Preselect "Сейчас" so the user who opens the drawer right after
  // marking a consumption tag doesn't have to click to pick a date —
  // one tap on the confirm button and they're done.
  const [currentValue, setCurrentValue] = useState(() => moment().format("DD.MM.YYYY HH:mm"));
  const [currentValueNote, setCurrentValueNote] = useState("");
  const [finished, setFinished] = useState(art.category === 1);
  const [activeQuickDate, setActiveQuickDate] = useState(0); // 0=now, 1/4/24=hours ago, "zero"=long ago, "release"=release date, null=manual
  const [pickDateWarning, setPickDateWarning] = useState(false);
  const refs = useRef({});

  // "Release date" quick-date option (movies + games where available).
  // Format: "YYYY-MM-DD". null = still loading / unknown; empty = the
  // backend couldn't produce anything for this art. Uses the parent's
  // prefetched value when supplied (ArtPage does this so the option is
  // visible the instant the Logbook opens), otherwise fetches locally.
  const [releaseDate, setReleaseDate] = useState(releaseDateProp ?? null);
  useEffect(() => {
    if (releaseDateProp !== undefined) { setReleaseDate(releaseDateProp); return; }
    if (art.category !== 1 && art.category !== 2) return;
    let cancelled = false;
    axiosInstance
      .get(`/getArtReleaseDate?artId=${art.id}`)
      .then((res) => { if (!cancelled) setReleaseDate(res.data?.releaseDate || ''); })
      .catch(() => { if (!cancelled) setReleaseDate(''); });
    return () => { cancelled = true; };
  }, [art.id, art.category, releaseDateProp]);

  const isDuplicateDate = (newDate) => {
    return dates.some(
      (date) =>
        moment(newDate).format("YYYY-MM-DD HH:mm") ===
        moment(date).format("YYYY-MM-DD HH:mm")
    );
  };

  const showPopup = (message) => {
    alert(message);
  };

  const handleAddDate = () => {
    if (activeQuickDate === undefined) {
      setPickDateWarning(true);
      return;
    }
    const parsedDate = moment(currentValue, ["DD.MM.YYYY HH:mm", "D MMM YYYY HH:mm"], true);
    if (!parsedDate.isValid()) {
      showPopup(language === "ru" ? "Выберите дату" : "Select date");
      return;
    }
    const newDate = parsedDate.toISOString();
    if (isDuplicateDate(newDate)) {
      showPopup(language === "ru" ? "Вы уже добавили такую же дату" : "You already added the same date");
      return;
    }
    const payload = { date: newDate, note: currentValueNote };
    if (art.category === 1 || art.category === 2 || art.category === 3) {
      payload.finished = finished;
    }
    axiosInstance
      .post(`/art/logbook/${art.id}/add`, payload)
      .then((response) => {
        setDates([response.data, ...dates]);
        setCurrentValue("");
        setCurrentValueNote("");
        setFinished(art.category === 1);
        setActiveQuickDate(undefined);
      })
      .catch((error) => {
        console.error("Failed to add date:", error);
      });
  };

  useEffect(() => {
    setFinished(art.category === 1);
  }, [art.category]);

  const handleQuickAddDate = (key) => {
    let newDate;
    if (key === "zero") {
      newDate = new Date(0);
    } else if (key === "release") {
      if (!releaseDate) return;
      // Anchor at noon local so tz shifts don't flip the day.
      newDate = moment(`${releaseDate} 12:00`, "YYYY-MM-DD HH:mm").toDate();
    } else {
      newDate = new Date();
      newDate.setHours(newDate.getHours() - key);
    }
    setCurrentValue(moment(newDate).format("DD.MM.YYYY HH:mm"));
    setActiveQuickDate(key);
    setPickDateWarning(false);
  };

  return (
    <div className="mt-1 w-full">
      <Paper p="md" radius="md">
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
          {[
            { key: 0, ru: "Сейчас", en: "Now" },
            { key: 1, ru: "Час назад", en: "1h ago" },
            { key: 4, ru: "4ч назад", en: "4h ago" },
            { key: 24, ru: "Вчера", en: "Yesterday" },
            { key: "zero", ru: "Давно", en: "Long ago" },
            ...((art.category === 1 || art.category === 2) && releaseDate
              ? [{ key: "release", ru: "Дата премьеры", en: "Release date" }]
              : []),
          ].map(item => {
            const isActive = activeQuickDate === item.key;
            const underline = isActive || pickDateWarning;
            const color = isActive
              ? 'var(--mantine-color-blue-text)'
              : pickDateWarning
                ? 'var(--mantine-color-red-text)'
                : undefined;
            return (
              <span
                key={item.key}
                className={`cursor-pointer text-sm ${underline ? 'underline decoration-solid' : ''} ${isActive ? 'font-medium' : ''}`}
                style={{ color, textDecorationColor: color }}
                onClick={() => handleQuickAddDate(item.key)}
              >
                {language === "ru" ? item.ru : item.en}
              </span>
            );
          })}
        </div>
        {pickDateWarning && (
          <div className="text-center text-xs mb-3" style={{ color: 'var(--mantine-color-red-text)' }}>
            {language === "ru" ? "Сначала выберите дату и время" : "Please select the date and time first"}
          </div>
        )}
        {activeQuickDate !== undefined && (
          <>
            <div className="flex justify-center items-center mt-4 mb-3">
              {new Date(currentValue).getTime() === 0 ? (
                <span className="text-lg font-medium">
                  {language === "ru" ? "Давно" : "Long time ago"}
                </span>
              ) : (
                <div className="w-[260px]">
                  <DateInput
                    value={currentValue}
                    onChange={(next) => {
                      setActiveQuickDate(null);
                      setCurrentValue(next);
                    }}
                    language={language}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-center">
              <TextInput
                placeholder={language === "ru" ? "Заметка (необязательно)" : "Note (optional)"}
                value={currentValueNote}
                onChange={(e) => setCurrentValueNote(e.target.value)}
                maxLength={100}
                size="sm"
                w={260}
                styles={{ input: { textAlign: 'center' } }}
              />
            </div>
            <div className="flex justify-center mt-2">
              <Button
                variant="filled"
                size="sm"
                onClick={handleAddDate}
              >
                {language === "ru" ? "Добавить дату" : "Add date"}
              </Button>
            </div>
          </>
        )}
      </Paper>
      {dates.length > 0 && (
        <div className="mt-6">
          <div className="text-center font-semibold mb-2" style={{ color: 'var(--mantine-color-dimmed)' }}>
            {language === "ru" ? "Добавленные даты" : "Added dates"}
          </div>
          <div className="space-y-2">
            {dates.map((dateTime) => {
              if (!refs.current[dateTime.id]) {
                refs.current[dateTime.id] = React.createRef();
              }
              return (
                <Paper key={dateTime.id} withBorder radius="md" p="sm" style={{ position: 'relative' }}>
                  <div className="text-center">
                    <div className="text-sm font-medium">
                      {new Date(dateTime.logTime).getTime() === 0
                        ? (language === "ru" ? "Давно" : "Long time ago")
                        : moment(dateTime.logTime).format("DD MMM YYYY, HH:mm")}
                    </div>
                    <input
                      type="text"
                      ref={refs.current[dateTime.id]}
                      className="text-xs w-full text-center mt-1 p-1 rounded focus:outline-none"
                      style={{ backgroundColor: 'transparent', color: 'var(--mantine-color-dimmed)', border: 'none' }}
                      value={dateTime.note || ""}
                      maxLength={100}
                      placeholder={language === "ru" ? "заметка..." : "note..."}
                      onChange={(e) => {
                        const updatedNote = e.target.value;
                        setDates(dates.map((d) =>
                          d.id === dateTime.id ? { ...d, note: updatedNote } : d
                        ));
                      }}
                      onBlur={() => {
                        axiosInstance
                          .post(`/art/logbook/${dateTime.id}/update`, { note: dateTime.note || "" })
                          .catch((error) => console.error("Failed to update note:", error));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          refs.current[dateTime.id].current.blur();
                        }
                      }}
                    />
                  </div>
                  <span
                    className="cursor-pointer flex items-center justify-center"
                    style={{ position: 'absolute', top: 8, right: 8, color: 'var(--mantine-color-red-text)' }}
                    onClick={() => {
                      const formattedDate = new Date(dateTime.logTime).getTime() === 0
                        ? (language === "ru" ? "Давно" : "Long time ago")
                        : moment(dateTime.logTime).format("DD MMM YYYY HH:mm");
                      const confirmMessage = language === "ru"
                        ? `Удалить дату: ${formattedDate}?`
                        : `Delete date: ${formattedDate}?`;
                      if (window.confirm(confirmMessage)) {
                        axiosInstance
                          .delete(`/art/logbook/${dateTime.id}/remove`)
                          .then(() => setDates(dates.filter((d) => d.id !== dateTime.id)))
                          .catch((error) => console.error("Failed to remove date:", error));
                      }
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </span>
                </Paper>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// DateInput — desktop lets the user edit the date as free-form text
// (select any substring, type, paste) because the native
// datetime-local segments are atomic and the user couldn't e.g. select
// the last two digits of the year to retype them. Mobile keeps the
// native datetime-local picker so the OS wheel/keyboard still works.
const DateInput = ({ value, onChange, language }) => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(pointer: fine)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(pointer: fine)');
    const sync = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const baseStyle = {
    width: '100%',
    height: 42,
    padding: '0 12px',
    textAlign: 'center',
    fontSize: 15,
    borderRadius: 8,
    border: '1px solid var(--mantine-color-default-border)',
    background: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-text)',
    colorScheme: 'dark light',
  };

  if (!isDesktop) {
    return (
      <input
        type="datetime-local"
        value={toDatetimeLocal(value)}
        onChange={(e) => onChange(fromDatetimeLocal(e.target.value))}
        style={baseStyle}
      />
    );
  }

  return <DateTextInput value={value} onChange={onChange} language={language} baseStyle={baseStyle} />;
};

// Free-form text input. Keeps a local draft while the user is typing;
// commits on blur / Enter. Shows a red border when the draft can't be
// parsed as a valid date-time; falls back to the last valid value on
// blur so the user never ends up with an unparseable string stuck in
// the field.
const DateTextInput = ({ value, onChange, language, baseStyle }) => {
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);
  // Sync the draft when the parent swaps the value (quick-date click
  // or a different art). Skip while the user is actively editing.
  const focusedRef = useRef(false);
  useEffect(() => { if (!focusedRef.current) setDraft(value); }, [value]);

  const commit = (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) { setInvalid(false); setDraft(value); return; }
    const parsed = moment(trimmed, ["DD.MM.YYYY HH:mm", "D.M.YYYY H:mm", "D MMM YYYY HH:mm"], true);
    if (!parsed.isValid()) { setInvalid(true); setDraft(value); return; }
    setInvalid(false);
    const next = parsed.format("DD.MM.YYYY HH:mm");
    setDraft(next);
    if (next !== value) onChange(next);
  };

  return (
    <input
      type="text"
      value={draft}
      inputMode="numeric"
      spellCheck={false}
      placeholder={language === 'ru' ? 'дд.мм.гггг чч:мм' : 'dd.mm.yyyy hh:mm'}
      onChange={(e) => { setDraft(e.target.value); if (invalid) setInvalid(false); }}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={(e) => { focusedRef.current = false; commit(e.target.value); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
      style={{
        ...baseStyle,
        borderColor: invalid ? 'var(--mantine-color-red-text)' : baseStyle.border?.split(' ')[2] || baseStyle.border,
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    />
  );
};

export default Logbook;
